import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authGet } from '../../../../services/apiService';

const FrameNote = ({ children }) => <p className="note">{children}</p>;

/** Full incident write-up, loaded with the portal login and shown in a script-free frame sized to its content. */
const IncidentReport = ({ name, incident }) => {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState('');
  const frameRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError('');
    (async () => {
      try {
        const body = await authGet(
          `/api/briefings/${encodeURIComponent(name)}/incidents/${encodeURIComponent(incident.id)}`,
          null,
          'text',
          { useCache: false }
        );
        if (!cancelled) setHtml(String(body));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the report');
      }
    })();
    return () => { cancelled = true; };
  }, [name, incident.id]);

  const fit = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame && frame.contentDocument;
    const height = doc && doc.documentElement ? doc.documentElement.scrollHeight : 0;
    if (height > 50) frame.style.height = `${height + 4}px`;
  }, []);

  if (error) return <FrameNote>Could not load the report: {error}</FrameNote>;
  if (html == null) return <FrameNote>Loading the report&hellip;</FrameNote>;
  return (
    <iframe
      ref={frameRef}
      className="inc-frame"
      title={incident.reportLabel || incident.title}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={fit}
    />
  );
};

const Fact = ({ fact }) => (
  <div>
    <b className={fact.tone}>
      {fact.value.map((segment, index) => (
        <React.Fragment key={`${segment.text}-${index}`}>
          {index > 0 ? ' ' : null}
          {segment.tone ? <span className={segment.tone}>{segment.text}</span> : segment.text}
        </React.Fragment>
      ))}
    </b>
    <span>{fact.label}</span>
  </div>
);

const BulletList = ({ items }) => (
  <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
);

const IncidentSummary = ({ incident }) => (
  <div className="inc-summary">
    <p className="lead">{incident.lead}</p>
    <div className="inc-facts">
      {incident.facts.map((fact) => <Fact fact={fact} key={fact.label} />)}
    </div>
    <BulletList items={incident.bullets} />
    {incident.extraTitle ? <p className="inc-extra"><strong>{incident.extraTitle}</strong></p> : null}
    {incident.extraBullets ? <BulletList items={incident.extraBullets} /> : null}
  </div>
);

/** The incidents list, one summary and full report at a time. */
export default function IncidentsView({ name, incidents }) {
  const [selected, setSelected] = useState(incidents[0].id);
  const incident = incidents.find((item) => item.id === selected) || incidents[0];
  return (
    <section>
      <div className="inc-list" role="tablist" aria-label="Incidents">
        {incidents.map((item) => (
          <button
            type="button"
            role="tab"
            className="inc-item"
            key={item.id}
            aria-selected={item.id === incident.id ? 'true' : 'false'}
            onClick={() => setSelected(item.id)}
          >
            <b>{item.title}</b>
            <span>{item.meta}</span>
          </button>
        ))}
      </div>
      <h2>Summary</h2>
      <IncidentSummary incident={incident} />
      <h2>{incident.reportTitle}</h2>
      <IncidentReport name={name} incident={incident} />
    </section>
  );
}
